import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Search, MapPin, LogIn, X, ScanLine, User, ClipboardList, CalendarClock, LogOut, UtensilsCrossed } from 'lucide-react';
import { screenService } from '@kss/api';
import { useCurrency } from '@kss/currency';
import { useAuth, logout } from '@kss/auth';
import { useCart, useSearch, cn, applyGuard } from '@kss/ui';
import { Logo } from './Logo';
import { QrScanModal } from './QrScanModal';
import { useTablePaths } from './useTablePaths';

const iconBtn =
  'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-content-secondary hover:bg-surface-card-hover';

/** Module-level cache so the header (shown on every page) fetches the home once. */
let cachedHome: { location: string; wallet: number } | null = null;

function useHeaderHome() {
  const [home, setHome] = useState(cachedHome);
  useEffect(() => {
    if (cachedHome) return;
    screenService
      .getCustomerHome()
      .then((d) => {
        cachedHome = { location: d.location, wallet: d.wallet };
        setHome(cachedHome);
      })
      .catch(() => {
        /* header falls back to its placeholders */
      });
  }, []);
  return home;
}

/** Avatar → dropdown menu: Profile · Orders · Booking · Logout. */
function UserMenu({ initials }: { initials: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const paths = useTablePaths();
  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <div ref={ref} className='relative flex-shrink-0'>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label='Account menu'
        className='flex h-[30px] w-[30px] items-center justify-center rounded-[9px] bg-[image:var(--gradient-primary)] font-display text-[11px] font-bold text-white'
      >
        {initials}
      </button>
      {open && (
        <div className='absolute right-0 top-[38px] z-50 w-48 overflow-hidden rounded-xl border border-border bg-surface-card py-1 shadow-pop'>
          <MenuRow icon={User} label='Profile' onClick={() => go('/account')} />
          <MenuRow icon={ClipboardList} label='Orders' onClick={() => go(paths.track)} />
          <MenuRow icon={CalendarClock} label='Booking' onClick={() => go('/booking')} />
          <div className='my-1 h-px bg-border' />
          <MenuRow
            icon={LogOut}
            label='Logout'
            danger
            onClick={() => {
              setOpen(false);
              dispatch(logout('user-signout'));
              navigate('/login', { replace: true });
            }}
          />
        </div>
      )}
    </div>
  );
}

function MenuRow({
  icon: Icon,
  label,
  onClick,
  danger
}: {
  icon: typeof User;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13px] font-semibold hover:bg-surface-card-hover ' +
        (danger ? 'text-danger' : 'text-content-secondary')
      }
    >
      <Icon size={15} /> {label}
    </button>
  );
}

export function WebHeader() {
  const { format } = useCurrency();
  const { isAuthenticated, user } = useAuth();
  const { count, subTotal, openCart } = useCart();
  const search = useSearch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [scanOpen, setScanOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const home = useHeaderHome();
  const paths = useTablePaths();

  const openSearch = () => {
    setSearchOpen(true);
    if (pathname !== '/') navigate('/'); // so filtered results are visible
  };

  return (
    <header className='sticky top-0 z-40 flex items-center gap-2 border-b border-border bg-surface-topbar px-3 py-3 md:gap-3 md:px-6'>
      <Link to='/' aria-label='Home' className='flex-shrink-0'>
        <Logo size='sm' />
      </Link>

      <button className='hidden flex-shrink-0 items-center gap-1 text-xs font-semibold text-content-secondary lg:flex'>
        {/* No invented location: this fell back to a literal "Koramangala" — the exact
            seed-on-failure pattern, on a customer-visible header. */}
        <MapPin size={13} /> {home?.location ?? '—'} ▾
      </button>

      {/* Current table (from the scanned QR / URL) — tap to open its menu. No scan ⇒ Takeaway. */}
      <Link
        to={paths.menu}
        aria-label={paths.table ? 'Your table' : 'Takeaway order'}
        title={paths.table ? `Table ${paths.table.toUpperCase()}` : 'Takeaway — scan a table QR for dine-in'}
        className='flex flex-shrink-0 items-center gap-1 rounded-full bg-primary-light px-2.5 py-1 font-display text-[11px] font-bold uppercase text-primary'
      >
        <UtensilsCrossed size={12} /> {paths.table ? paths.table : 'Takeaway'}
      </Link>

      {/* Search trigger — tapping opens the full-width search overlay. */}
      <button
        onClick={openSearch}
        aria-label='Search dishes'
        className='flex min-w-0 flex-1 items-center gap-2 rounded-lg bg-surface-card-hover px-3 py-2 text-left'
      >
        <Search size={14} className='flex-shrink-0 text-content-muted' />
        <span className={cn('min-w-0 flex-1 truncate text-xs', search.query ? 'text-content-primary' : 'text-content-muted')}>
          {search.query || 'Search dishes…'}
        </span>
      </button>

      {/* Scan a table QR to open that table's menu. */}
      <button onClick={() => setScanOpen(true)} aria-label='Scan table QR' className={iconBtn}>
        <ScanLine size={17} />
      </button>

      {isAuthenticated ? (
        <UserMenu initials={user?.initials ?? 'ME'} />
      ) : (
        <Link to='/login' aria-label='Log in' className={iconBtn}>
          <LogIn size={16} />
        </Link>
      )}

      {/* Cart is a signed-in-customer feature — hidden when logged out (the cart is
          per-customer and cleared on logout, so a guest never sees a stale count). */}
      {isAuthenticated && (
        <button
          onClick={openCart}
          className='relative flex-shrink-0 rounded-lg bg-content-primary px-3 py-2 font-display text-xs font-bold text-surface-card'
        >
          {format(subTotal)}
          {count > 0 && (
            <span className='absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-extrabold text-white'>
              {count}
            </span>
          )}
        </button>
      )}

      <QrScanModal open={scanOpen} onClose={() => setScanOpen(false)} />

      {/* Full-width search overlay — takes over the top bar with a close (✕); the filtered
          menu stays visible below so results update live as you type. */}
      {searchOpen && (
        <div className='fixed inset-x-0 top-0 z-[70] border-b border-border bg-surface-topbar shadow-card'>
          <div className='mx-auto flex max-w-[1200px] items-center gap-2 px-3 py-3.5 md:px-6'>
            <Search size={18} className='flex-shrink-0 text-content-muted' />
            <input
              autoFocus
              value={search.query}
              onChange={(e) => search.setQuery(applyGuard(e.target.value, 'text', 80))}
              onKeyDown={(e) => e.key === 'Escape' && setSearchOpen(false)}
              maxLength={80}
              aria-label='Search dishes, cuisines'
              placeholder='Search dishes, cuisines…'
              className='min-w-0 flex-1 bg-transparent text-base text-content-primary placeholder:text-content-muted focus:outline-none'
            />
            {search.query && (
              <button
                onClick={() => search.setQuery('')}
                aria-label='Clear'
                className='flex-shrink-0 rounded-md px-2 py-1 text-[12px] font-semibold text-content-muted hover:text-primary'
              >
                Clear
              </button>
            )}
            <button
              onClick={() => setSearchOpen(false)}
              aria-label='Close search'
              className='flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-content-secondary hover:bg-surface-card-hover'
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
